import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Grid,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  Settings as SettingsIcon,
  Send as SendIcon,
  Info as InfoIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import { ja } from 'date-fns/locale';
import { apiService } from '../services/api';

interface AlertConfig {
  thresholds: {
    [key: string]: number;
  };
  cooldown_minutes: number;
  last_alert_times: {
    [key: string]: string;
  };
}

interface MonitoringAlertsProps {
  open: boolean;
  onClose: () => void;
}

const thresholdLabels: { [key: string]: string } = {
  error_rate_percent: 'エラー率 (%)',
  critical_errors: '重要エラー数/時間',
  avg_response_time_ms: '平均レスポンス時間 (ms)',
  slow_request_percent: '遅いリクエスト率 (%)',
  storage_usage_percent: 'ストレージ使用率 (%)',
  failed_logins: 'ログイン失敗回数',
};

const MonitoringAlerts: React.FC<MonitoringAlertsProps> = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState<AlertConfig | null>(null);
  const [editedThresholds, setEditedThresholds] = useState<{ [key: string]: number }>({});
  const [testResult, setTestResult] = useState<any>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const data = await apiService.getMonitoringAlertConfig() as AlertConfig;
      setConfig(data);
      setEditedThresholds(data.thresholds || {});
    } catch (err) {
      setError(err instanceof Error ? err.message : '設定の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const updateThreshold = async (threshold: string, value: number) => {
    setSaving(true);
    setError(null);
    
    try {
      await apiService.updateMonitoringAlertThreshold(threshold, value);
      await fetchConfig();
    } catch (err) {
      setError(err instanceof Error ? err.message : '閾値の更新に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const testAlerts = async () => {
    setLoading(true);
    setError(null);
    setTestResult(null);
    
    try {
      const data = await apiService.testMonitoringAlerts() as any;
      setTestResult(data.result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'テスト送信に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      fetchConfig();
    }
  }, [open]);

  const handleThresholdChange = (key: string, value: string) => {
    const numValue = parseFloat(value);
    if (!isNaN(numValue)) {
      setEditedThresholds({ ...editedThresholds, [key]: numValue });
    }
  };

  const handleSaveThreshold = async (key: string) => {
    if (editedThresholds[key] !== config?.thresholds[key]) {
      await updateThreshold(key, editedThresholds[key]);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box display="flex" alignItems="center">
            <SettingsIcon sx={{ mr: 1 }} />
            <Typography variant="h6">アラート設定</Typography>
          </Box>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent dividers>
        {loading && !config ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : config ? (
          <Box>
            <Typography variant="h6" gutterBottom>
              アラート閾値設定
            </Typography>
            
            <Grid container spacing={2} mb={3}>
              {Object.entries(config.thresholds).map(([key, value]) => (
                <Grid item xs={12} md={6} key={key}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                        {thresholdLabels[key] || key}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={1}>
                        <TextField
                          type="number"
                          size="small"
                          value={editedThresholds[key] || 0}
                          onChange={(e) => handleThresholdChange(key, e.target.value)}
                          disabled={saving}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleSaveThreshold(key)}
                          disabled={saving || editedThresholds[key] === value}
                        >
                          保存
                        </Button>
                      </Box>
                      {config.last_alert_times[key] && (
                        <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
                          最終アラート: {formatDistanceToNow(new Date(config.last_alert_times[key]), { locale: ja, addSuffix: true })}
                        </Typography>
                      )}
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>

            <Box mb={3}>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                クールダウン時間
              </Typography>
              <Typography variant="body1">
                {config.cooldown_minutes}分（同じアラートの再送信までの待機時間）
              </Typography>
            </Box>

            <Box>
              <Typography variant="h6" gutterBottom>
                アラートテスト
              </Typography>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={testAlerts}
                disabled={loading}
              >
                テストアラートを送信
              </Button>
              
              {testResult && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    テスト結果
                  </Typography>
                  <Typography variant="body2">
                    送信アラート数: {testResult.alerts_sent || 0}
                  </Typography>
                  {testResult.alerts && testResult.alerts.length > 0 && (
                    <Box mt={1}>
                      {testResult.alerts.map((alert: any, index: number) => (
                        <Chip
                          key={index}
                          label={alert.type}
                          size="small"
                          color={alert.severity === 'critical' ? 'error' : 'warning'}
                          sx={{ mr: 0.5, mb: 0.5 }}
                        />
                      ))}
                    </Box>
                  )}
                </Alert>
              )}
            </Box>

            <Alert severity="info" icon={<InfoIcon />} sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                アラート通知について
              </Typography>
              <Typography variant="body2">
                • アラートはSlackの #system-alerts チャンネルに送信されます
              </Typography>
              <Typography variant="body2">
                • 15分ごとに監視項目がチェックされます
              </Typography>
              <Typography variant="body2">
                • 同じアラートは{config.cooldown_minutes}分間再送信されません
              </Typography>
            </Alert>
          </Box>
        ) : null}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>閉じる</Button>
      </DialogActions>
    </Dialog>
  );
};

export default MonitoringAlerts;